import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, IconButton, Paper, Typography } from '@mui/material'
import { addNotifications, dismissNotification } from '../../reducer/globalNotification.reducer'
import { selectNewGlobalNotifications } from '../../selectors/globalNotification'
import { Category, GlobalNotificationType } from '../../proto/generated/global-notification'
import ThinArrowLeftIcon from '../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import ClearIcon from '../../assets/icons/ClearIcon'
import { UltraSmallButton } from '../custom'
import { white } from '../../assets/colors'
import { handleGetAllActiveGlobalNotifications } from '../../services/apis/globalNotification'

const Navbar = () => {
    const dispatch = useDispatch()
    const notifications = useSelector(selectNewGlobalNotifications).filter((notification) => notification.type === GlobalNotificationType.SYSTEM_NOTIFICATION)
    const notificationsRef = useRef(notifications)
    const [visible, setVisible] = useState(true)
    const [index, setIndex] = useState(0)
    useEffect(() => {
        notificationsRef.current = notifications
    }, [notifications])

    useEffect(() => {
        const timeout = setTimeout(async () => {
            if (!notificationsRef.current || notificationsRef.current.length === 0) {
                dispatch(addNotifications(await handleGetAllActiveGlobalNotifications()))
            } else {
                console.error('Already present in Redux')
            }
        }, 2000)

        return () => clearTimeout(timeout)
    }, [])

    const handleCloseButton = useCallback(() => {
        if (notifications.length > 0) {
            const notificationIds = notifications.map((n: any) => n.id)
            dispatch(dismissNotification(notificationIds as any))
        }
        setVisible(false)
    }, [notifications, index, dispatch])

    const headerVisible = useMemo(() => {
        return !(!visible || notifications.length === 0)
    }, [notifications.length, visible])

    const handleNext = () => {
        setIndex((prev) => (prev + 1) % notifications.length)
    }

    const handlePrev = () => {
        setIndex((prev) => (prev - 1 + notifications.length) % notifications.length)
    }
    const CATEGORY_TO_SEVERITY: Record<Category, 'info' | 'warning' | 'error'> = {
        [Category.CATEGORY_UNKNOWN]: 'info',
        [Category.INFO]: 'info',
        [Category.WARN]: 'warning',
        [Category.ERROR]: 'error',
        [Category.UNRECOGNIZED]: 'info'
    }

    return (
        <Paper style={{ gridArea: 'header', padding: 0 }}>
            <Box
                component="nav"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: headerVisible ? '30px' : 0,
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                {headerVisible ? (
                    <>
                        <Alert
                            severity={CATEGORY_TO_SEVERITY[notifications[index]?.category as Category]}
                            variant="filled"
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                '& .MuiAlert-message': { flexGrow: 1 },
                                '& .MuiAlert-icon': { marginRight: '7px' },
                                color: '#fff'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'space-between' }}>
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {notifications[index]?.message}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {notifications.length > 1 && (
                                        <>
                                            <Typography variant="body2" mr={1}>
                                                {`${index + 1}/${notifications.length}`}
                                            </Typography>

                                            <IconButton size="small" onClick={handlePrev} disabled={index === 0}>
                                                <ThinArrowLeftIcon sx={{ color: white }} />
                                            </IconButton>

                                            <IconButton size="small" onClick={handleNext} disabled={index === notifications.length - 1}>
                                                <ThinArrowRightIcon sx={{ color: white }} />
                                            </IconButton>
                                        </>
                                    )}
                                    <UltraSmallButton size="small" onClick={handleCloseButton}>
                                        <ClearIcon sx={{ color: white, fontSize: '15px' }} />
                                    </UltraSmallButton>
                                </Box>
                            </Box>
                        </Alert>
                    </>
                ) : null}
            </Box>
        </Paper>
    )
}

export default React.memo(Navbar)
